import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './components/pages/Login';
import Signup from './components/pages/Signup';
import RoleSelection from './components/pages/RoleSelection';
import Kyc from './components/pages/Kyc';
import TermsAcceptance from './components/pages/TermsAcceptance';
import AuthChoice from './components/pages/AuthChoice';
import Messages from './components/pages/Messages';
import Profile from './components/pages/Profile';
import FreelancerDashboard from './components/pages/FreelancerDashboard';
import FreelancerEarnings from './components/pages/FreelancerEarnings';
import BusinessPublicProfile from './components/pages/BusinessPublicProfile';
import FreelancerPublicProfile from './components/pages/FreelancerPublicProfile';
import BusinessDashboard from './components/pages/BusinessDashboard';
import JobPage from './components/pages/JobPage';
import Homepage from './components/pages/Homepage';
import NotFound from './components/pages/NotFound';
import ChatPage from './components/pages/ChatPage';
import InboxCounter from './components/common/InboxCounter';
import AdminPage from './components/pages/AdminPage';
import Unauthorized from './components/pages/Unauthorized';
import VersionCheck from './components/common/VersionCheck';
import Navigation from './components/common/Navigation';
import AddToHomescreenPrompt from './components/common/AddToHomescreenPrompt';
import FaviconUpdater from './components/common/FaviconUpdater';
import HelpButton from './components/common/HelpButton';
import './App.css';

function AppRoutes() {
  const { currentUser, userRoles, currentRole, loading, isApproved } = useAuth();
  const [businessTypeCheck, setBusinessTypeCheck] = useState({ loading: true, hasBusinessType: true });
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const [checkingRoleSelection, setCheckingRoleSelection] = useState(true);
  const [kycCheck, setKycCheck] = useState({ loading: true, required: false, completed: true });
  const location = useLocation();

  // Check if user needs role selection
  useEffect(() => {
    const checkRoleSelectionNeeded = async () => {
      if (loading) {
        setCheckingRoleSelection(true);
        return;
      }

      if (!currentUser) {
        setNeedsRoleSelection(false);
        setCheckingRoleSelection(false);
        return;
      }

      const isAdmin = userRoles && userRoles.includes('admin');
      const isApprovedUser = isAdmin || !!isApproved;

      if (!isApprovedUser) {
        setNeedsRoleSelection(false);
        setCheckingRoleSelection(false);
        return;
      }

      // Check if user has no roles or needs to fill business requirements
      if (userRoles && userRoles.length === 0) {
        setNeedsRoleSelection(true);
        setCheckingRoleSelection(false);
        return;
      }

      // Check if business user is missing business type or display name
      if (userRoles && userRoles.includes('business')) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('./firebase');
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const missingBusinessType = !userData.businessType || !userData.businessType.trim();
            const missingDisplayName = !userData.displayName || !userData.displayName.trim();
            if (missingBusinessType || missingDisplayName) {
              setNeedsRoleSelection(true);
              setCheckingRoleSelection(false);
              return;
            }
          } else {
            // User document doesn't exist, needs role selection
            setNeedsRoleSelection(true);
            setCheckingRoleSelection(false);
            return;
          }
        } catch (error) {
          console.error('Error checking business requirements:', error);
          // On error, assume needs role selection to be safe
          setNeedsRoleSelection(true);
          setCheckingRoleSelection(false);
          return;
        }
      }

      setNeedsRoleSelection(false);
      setCheckingRoleSelection(false);
    };

    checkRoleSelectionNeeded();
  }, [currentUser, userRoles, loading, isApproved, location.pathname]);

  // Check if business user has business type and display name
  useEffect(() => {
    const checkBusinessRequirements = async () => {
      if (currentUser && userRoles && userRoles.includes('business')) {
        // Set loading only for business users
        setBusinessTypeCheck(prev => ({ ...prev, loading: true }));

        try {
          const { doc, getDoc, getDocFromServer } = await import('firebase/firestore');
          const { db } = await import('./firebase');
          // Use getDocFromServer to force a fresh fetch from the server, bypassing cache
          // This ensures we get the latest data after profile updates
          const userDoc = await getDocFromServer(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const hasBusinessType = userData.businessType && String(userData.businessType).trim();
            // For access control we only require a valid businessType; displayName is handled separately in Login flow
            setBusinessTypeCheck({ loading: false, hasBusinessType: !!hasBusinessType });
          } else {
            setBusinessTypeCheck({ loading: false, hasBusinessType: false });
          }
        } catch (error) {
          console.error('Error checking business requirements:', error);
          // If getDocFromServer fails (e.g., offline), fall back to regular getDoc
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('./firebase');
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const hasBusinessType = userData.businessType && String(userData.businessType).trim();
              setBusinessTypeCheck({ loading: false, hasBusinessType: !!hasBusinessType });
            } else {
              setBusinessTypeCheck({ loading: false, hasBusinessType: false });
            }
          } catch (fallbackError) {
            console.error('Error in fallback check:', fallbackError);
            setBusinessTypeCheck({ loading: false, hasBusinessType: false });
          }
        }
      } else {
        // Not a business user or no roles, allow access immediately
        setBusinessTypeCheck({ loading: false, hasBusinessType: true });
      }
    };

    if (!loading && currentUser && userRoles) {
      checkBusinessRequirements();
    } else if (!loading && !currentUser) {
      setBusinessTypeCheck({ loading: false, hasBusinessType: true });
    }
  }, [currentUser, userRoles, loading, location.pathname]);

  // Check if user must complete KYC (Sumsub)
  useEffect(() => {
    const checkKyc = async () => {
      if (loading) {
        setKycCheck(prev => ({ ...prev, loading: true }));
        return;
      }
      if (!currentUser) {
        setKycCheck({ loading: false, required: false, completed: true });
        return;
      }

      const isAdmin = userRoles && userRoles.includes('admin');
      const isApprovedUser = isAdmin || !!isApproved;

      try {
        const { doc, getDocFromServer } = await import('firebase/firestore');
        const { db } = await import('./firebase');
        // Use getDocFromServer to force fresh fetch, bypassing cache
        const userDoc = await getDocFromServer(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) {
          // New user document doesn't exist - require KYC
          setKycCheck({ loading: false, required: true, completed: false });
          return;
        }
        const userData = userDoc.data() || {};
        // KYC is required if:
        // 1. kycRequired is explicitly true, OR
        // 2. kycCompleted field doesn't exist (legacy users or new users)
        // KYC is completed only if kycCompleted is explicitly true
        const completed = userData.kycCompleted === true;
        const kycRequiredInDb = userData.kycRequired === true || !('kycCompleted' in userData);

        // Require KYC if:
        // - It's required in DB AND user is approved (to access dashboards), OR
        // - User has roles but KYC is not completed (even if not approved yet, they should complete it)
        const hasRoles = userRoles && userRoles.length > 0;
        const required = (kycRequiredInDb && isApprovedUser) || (hasRoles && !completed);

        setKycCheck({ loading: false, required, completed });
      } catch (e) {
        // If offline / error, fail closed to require KYC (safer)
        console.warn('Failed to check KYC status:', e);
        setKycCheck({ loading: false, required: true, completed: false });
      }
    };

    if (!loading) {
      checkKyc();
    }

    // Refresh KYC check immediately when navigating to /kyc (after role selection)
    // This ensures we get the latest status right away
    if (currentUser && !loading && location.pathname === '/kyc') {
      // Immediate refresh for /kyc page
      const immediateTimeout = setTimeout(() => {
        checkKyc();
      }, 100);
      return () => clearTimeout(immediateTimeout);
    }

    // Refresh KYC check when navigating away from /kyc
    // This ensures we get the latest status after completion
    if (currentUser && !loading && location.pathname !== '/kyc') {
      const timeoutId = setTimeout(() => {
        checkKyc();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [currentUser, userRoles, isApproved, loading, location.pathname]);

  // Show loading while auth state is being determined
  if (loading || businessTypeCheck.loading || checkingRoleSelection || kycCheck.loading) {
    return (
      <div style={{
        display: 'flex',
        backgroundColor: "lightblue",
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'white',
        color: 'black',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to role selection if needed (but not if already on role-selection page)
  if (needsRoleSelection && location.pathname !== '/role-selection' &&
      location.pathname !== '/login' && location.pathname !== '/signup' &&
      location.pathname !== '/auth-choice' && location.pathname !== '/') {
    return <Navigate to="/role-selection" replace />;
  }

  // Redirect to KYC if required (but allow the KYC page + auth pages + public pages)
  // Block ALL protected routes if KYC is required but not completed
  // Don't block while KYC check is still loading
  const isPublicPage =
    location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/auth-choice' ||
    location.pathname === '/role-selection' ||
    location.pathname === '/terms' ||
    location.pathname === '/kyc' ||
    location.pathname === '/unauthorized' ||
    (location.pathname.startsWith('/business/') && location.pathname.endsWith('/public')) ||
    (location.pathname.startsWith('/freelancer/') && location.pathname.endsWith('/public'));

  // Only redirect if KYC check is complete (not loading) and KYC is required but not completed
  if (!kycCheck.loading && kycCheck.required && !kycCheck.completed && !isPublicPage) {
    return <Navigate to="/kyc" replace />;
  }

  // If user is on /kyc but KYC is completed, redirect to dashboard
  if (kycCheck.completed && location.pathname === '/kyc') {
    const role = currentRole || (userRoles || [])[0] || null;
    const targetPath = role === 'freelancer'
      ? '/freelancer/dashboard'
      : (role === 'business' || role === 'employer')
        ? '/business/dashboard'
        : '/profile';
    return <Navigate to={targetPath} replace />;
  }

  // Check if business user is missing business type
  // Allow access if: user has roles, is approved, AND (not a business user OR has business type OR still checking)
  // Also check KYC: if KYC is required, it MUST be completed (not just loading) to access dashboard
  const isAdmin = userRoles && userRoles.includes('admin');
  const isApprovedUser = isAdmin || !!isApproved;
  const canAccessDashboard = currentUser &&
    userRoles &&
    userRoles.length > 0 &&
    isApprovedUser &&
    (!userRoles.includes('business') || businessTypeCheck.hasBusinessType || businessTypeCheck.loading) &&
    // KYC must be completed if required (not just loading)
    // Don't block if KYC check is still loading
    (!kycCheck.required || (kycCheck.completed && !kycCheck.loading) || kycCheck.loading);

  // Check if InboxCounter is shown (same logic as below)
  const shouldShowInboxCounter = currentUser &&
    !(location.pathname || '').startsWith('/chat') &&
    !(location.pathname || '').startsWith('/login') &&
    !(location.pathname || '').startsWith('/admin') &&
    !(location.pathname || '').startsWith('/auth-choice') &&
    !(location.pathname || '').startsWith('/role-selection') &&
    !(location.pathname || '').startsWith('/terms') &&
    !(location.pathname || '').startsWith('/kyc') &&
    !(location.pathname || '').endsWith('/');

  return (
    <>
      <VersionCheck />
      <AddToHomescreenPrompt />
      <Navigation />
      {/* Show floating help button only when InboxCounter is NOT shown */}
      {!shouldShowInboxCounter && <HelpButton />}
      <Routes>
        <Route
          path="/"
          element={<Homepage />}
        />
        <Route
          path="/admin"
          element={<AdminPage />}
        />
        <Route
          path="/auth-choice"
          element={<AuthChoice />}
        />
        <Route
          path="/login"
          element={<Login />}
        />
        <Route
          path="/signup"
          element={<Signup />}
        />
        <Route
          path="/role-selection"
          element={<RoleSelection />}
        />
        <Route
          path="/terms"
          element={<TermsAcceptance />}
        />
        <Route
          path="/kyc"
          element={<Kyc />}
        />
        <Route
          path="/jobs"
          element={
            canAccessDashboard ? (
              currentUser && (currentRole === 'business' || currentRole === 'employer' || (userRoles && (userRoles.includes('business') || userRoles.includes('employer')))) ? (
                <Navigate to="/business/dashboard" replace />
              ) : (
                <Messages />
              )
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/chat"
          element={
            canAccessDashboard ? (
              <ChatPage />
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/profile"
          element={
            canAccessDashboard ? (
              <Profile />
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/freelancer/dashboard"
          element={
            canAccessDashboard ? (
              <FreelancerDashboard />
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/freelancer/earnings"
          element={
            canAccessDashboard ? (
              <FreelancerEarnings />
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/business/dashboard"
          element={
            canAccessDashboard ? (
              <BusinessDashboard />
            ) : (
              (!kycCheck.loading && kycCheck.required && !kycCheck.completed) ? (
                <Navigate to="/kyc" replace />
              ) : (
                <Navigate to="/unauthorized" />
              )
            )
          }
        />
        <Route
          path="/unauthorized"
          element={<Unauthorized />}
        />
        <Route
          path="/business/:businessId/public"
          element={<BusinessPublicProfile />}
        />
        <Route
          path="/freelancer/:freelancerId/public"
          element={<FreelancerPublicProfile />}
        />
        <Route
          path="/job/:conversationId"
          element={
            canAccessDashboard ? (
              <JobPage />
            ) : (
              <Navigate to="/unauthorized" />
            )
          }
        />
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
      {currentUser &&
        !(location.pathname || '').startsWith('/chat') &&
        !(location.pathname || '').startsWith('/login') &&
        !(location.pathname || '').startsWith('/admin') &&
        !(location.pathname || '').startsWith('/auth-choice') &&
        !(location.pathname || '').startsWith('/role-selection') &&
        !(location.pathname || '').startsWith('/kyc') &&
        !(location.pathname || '').endsWith('/') &&
        <InboxCounter />}
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <FaviconUpdater />
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
